import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawerNew extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }


  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

/**
 * Initialize QB Ajax Filters
 * Exact copy of logic from _header-menu.liquid
 */
/**
 * Initialize QB Ajax Filters
 * Consolidated logic for both desktop and mobile menus
 */
function initQbFilters() {
  const containers = document.querySelectorAll('.qb-ajax-filter-container');
  if (containers.length === 0) return;

  containers.forEach((container) => {
    // @ts-ignore
    const containerEl = /** @type {HTMLElement} */ (container);
    const status = containerEl.dataset.status;
    const isMobile = containerEl.dataset.mobileMode === 'true';

    // If already loaded and NOT mobile, skip. 
    // If mobile, we might need to re-run distribution if targets were missed initially.
    if (status === 'loaded' && !isMobile) return;
    if (status === 'loading') return;

    // If mobile and already loaded, only continue if we still see placeholders in the sibling list
    if (isMobile && status === 'loaded') {
      const siblingList = containerEl.nextElementSibling;
      if (!siblingList || !siblingList.querySelector('.qb-loading-placeholder')) return;
      // If we are here, we need to try distributing again (targets might have appeared)
    } else {
      // Start loading
      containerEl.dataset.status = 'loading';
    }

    const url = container.getAttribute('data-url');
    if (!url) {
      if (isMobile) removePlaceholders(container);
      // @ts-ignore
      container.dataset.status = 'error';
      return;
    }

    // If mobile and status was 'loaded' (we are just re-distributing), we don't need to fetch again
    if (isMobile && status === 'loaded') {
      distributeMobileFilters(container);
      container.dispatchEvent(new CustomEvent('qb-filters-loaded', { bubbles: true }));
      return;
    }

    const timestamp = new Date().getTime();
    const fetchUrl = url.includes('?')
      ? `${url}&section_id=qb-filter-renderer&_=${timestamp}`
      : `${url}?section_id=qb-filter-renderer&_=${timestamp}`;

    fetch(fetchUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseText, 'text/html');
        let content = doc.querySelector('.qb-filter-content');

        if (!content) {
          const sectionWrapper = doc.querySelector('#shopify-section-qb-filter-renderer');
          if (sectionWrapper) {
            content = sectionWrapper.querySelector('.qb-filter-content');
          }
        }

        if (content) {
          // @ts-ignore
          content.style.display = '';
          container.innerHTML = content.innerHTML;

          // Apply common filter naming/cleanup logic
          processFilterContent(container);

          if (isMobile) {
            distributeMobileFilters(container);
          }
          // @ts-ignore
          container.dataset.status = 'loaded';
          container.classList.add('qb-fade-in');
          container.dispatchEvent(new CustomEvent('qb-filters-loaded', { bubbles: true }));
        } else {
          console.warn('QB: No content found for', url);
          if (isMobile) {
            removePlaceholders(container, 'No filters found.');
          } else {
            container.innerHTML = '<div style="padding:20px;">No filters found.</div>';
          }
          // @ts-ignore
          container.dataset.status = 'error';
        }
      })
      .catch((err) => {
        console.error('QB: Error fetching filters:', err);
        if (isMobile) {
          removePlaceholders(container, `Error loading filters.`);
        } else {
          container.innerHTML = `<div style="padding:20px; color: red;">Error: ${err.message}</div>`;
        }
        // @ts-ignore
        container.dataset.status = 'error';
        container.dispatchEvent(new CustomEvent('qb-filters-loaded', { bubbles: true }));
      });
  });
}

/**
 * Process and clean up filter content names and headings
 * @param {Element} container 
 */
function processFilterContent(container) {
  const productTitleRaw = container.getAttribute('data-product-title');
  let productTitle = productTitleRaw;
  if (productTitleRaw) {
    const parts = productTitleRaw.split(' ').filter(Boolean);
    productTitle = parts.slice(0, 2).join(' ');
  }
  const columns = container.querySelectorAll('.qb-filter-column');
  const menuHandle = (container.getAttribute('data-menu-handle') || '').toLowerCase();
  const manualHeading1 = container.getAttribute('data-heading-1') || '';
  const manualHeading2 = container.getAttribute('data-heading-2') || '';
  const manualHeading3 = container.getAttribute('data-heading-3') || '';
  const manualHeading4 = container.getAttribute('data-heading-4') || '';
  const headingsArr = [manualHeading1, manualHeading2, manualHeading3, manualHeading4].filter(Boolean);

  /** @type {Record<string, string>} */
  const headingMap = {};

  // Always use keyword mapping as it's more flexible
  headingsArr.forEach((h) => {
    const hl = h.toLowerCase();
    if (hl.indexOf('serie') !== -1) headingMap.serie = h;
    else if (hl.indexOf('land') !== -1) headingMap.land = h;
    else if (hl.indexOf('material') !== -1) headingMap.material = h;
    else if (hl.indexOf('feingewicht') !== -1 || hl.indexOf('gewicht') !== -1) headingMap.gewicht = h;
  });

  // Handle-specific fallbacks for legacy support/fixed structures
  if (menuHandle === 'gold' || menuHandle === 'silber') {
    if (!headingMap.gewicht && manualHeading1) headingMap.gewicht = manualHeading1;
    if (!headingMap.land && manualHeading2) headingMap.land = manualHeading2;
  } else if (menuHandle === 'barren') {
    if (!headingMap.gewicht && manualHeading1) headingMap.gewicht = manualHeading1;
    if (!headingMap.material && manualHeading2) headingMap.material = manualHeading2;
  }
  const hasManualHeadings = Object.keys(headingMap).length > 0;

  columns.forEach((column) => {
    const filterLabel = (column.getAttribute('data-filter-label') || '').toLowerCase();
    const heading = column.querySelector('.qb-filter-heading');
    let typeKey = '';
    if (filterLabel.indexOf('serie') !== -1) typeKey = 'serie';
    else if (filterLabel.indexOf('land') !== -1) typeKey = 'land';
    else if (filterLabel.indexOf('material') !== -1) typeKey = 'material';
    else if (filterLabel.indexOf('feingewicht') !== -1 || filterLabel.indexOf('gewicht') !== -1) typeKey = 'gewicht';

    if (hasManualHeadings) {
      if (!typeKey || !headingMap[typeKey]) {
        // @ts-ignore
        column.style.display = 'none';
        return;
      }
      if (heading) heading.textContent = headingMap[typeKey] || null;
    } else if (heading && productTitle) {
      heading.textContent = productTitle || null;
    }

    const links = column.querySelectorAll('.qb-filter-link');
    links.forEach((link) => {
      const valueLabel = link.getAttribute('data-filter-value-label') || link.textContent?.trim() || '';
      const countSpan = link.querySelector('span');
      let newText;

      if (filterLabel.indexOf('feingewicht') !== -1 && productTitle) {
        newText = productTitle + ' (' + valueLabel + ')';
      } else if (filterLabel.indexOf('land') !== -1 && productTitle) {
        newText = valueLabel + ' ' + productTitle;
      }

      if (newText) {
        if (countSpan) countSpan.remove();
        link.textContent = newText;
        if (countSpan) link.appendChild(countSpan);
      }
    });
  });
}

/**
 * Distribute filters to mobile targets
 * @param {Element} container 
 */
function distributeMobileFilters(container) {
  const siblingList = container.nextElementSibling;
  if (!siblingList || !siblingList.classList.contains('menu-drawer__menu--childlist')) return;

  const columns = container.querySelectorAll('.qb-filter-column');
  const listItems = Array.from(siblingList.children);

  columns.forEach((column) => {
    // @ts-ignore
    if (column.style.display === 'none') return;
    const filterLabel = (column.getAttribute('data-filter-label') || '').toLowerCase();
    let typeKey = '';
    if (filterLabel.indexOf('serie') !== -1) typeKey = 'serie';
    else if (filterLabel.indexOf('land') !== -1) typeKey = 'land';
    else if (filterLabel.indexOf('material') !== -1) typeKey = 'material';
    else if (filterLabel.indexOf('feingewicht') !== -1 || filterLabel.indexOf('gewicht') !== -1) typeKey = 'gewicht';

    let targetLi = listItems.find(li => {
      const title = (li.getAttribute('data-child-title') || '').toLowerCase();
      if (typeKey === 'serie' && (title.indexOf('serie') !== -1 || title.indexOf('series') !== -1)) return true;
      if (typeKey === 'land' && (title.indexOf('land') !== -1 || title.indexOf('country') !== -1)) return true;
      if (typeKey === 'material' && title.indexOf('material') !== -1) return true;
      if (typeKey === 'gewicht' && (title.indexOf('gewicht') !== -1 || title.indexOf('weight') !== -1 || title.indexOf('feingewicht') !== -1)) return true;
      return false;
    });

    if (targetLi) {
      const targetPlace = targetLi.querySelector('.qb-mobile-target');
      if (targetPlace) {
        const list = column.querySelector('ul');
        if (list) {
          targetPlace.innerHTML = '';
          targetPlace.classList.add('qb-filter-column');

          // Add a "View All" link at the beginning of the list if it doesn't already exist
          const childUrl = targetLi.getAttribute('data-child-url');
          const childTitle = targetLi.getAttribute('data-child-title');
          if (childUrl && childTitle && !list.querySelector('.menu-drawer__list-item--parent-link')) {
            const parentLi = document.createElement('li');
            parentLi.className = 'menu-drawer__list-item menu-drawer__list-item--parent-link';
            parentLi.innerHTML = `
              <a href="${childUrl}" class="menu-drawer__menu-item menu-drawer__menu-item--child focus-inset">
                <span class="menu-drawer__menu-item-text wrap-text">${childTitle}</span>
              </a>
            `;
            list.insertBefore(parentLi, list.firstChild);
          }

          // @ts-ignore
          list.style.margin = '0';
          // @ts-ignore
          list.style.paddingLeft = '0';
          // @ts-ignore
          list.style.listStyle = 'none';
          targetPlace.appendChild(list);
        }
      }
    }
  });

  // Finally, clean up any remaining placeholders that didn't get a match
  removePlaceholders(container);
}

/**
 * Clean up loading placeholders
 * @param {Element} container 
 * @param {string} [message]
 */
function removePlaceholders(container, message) {
  const siblingList = container.nextElementSibling;
  if (!siblingList || !siblingList.classList.contains('menu-drawer__menu--childlist')) return;
  
  const placeholders = siblingList.querySelectorAll('.qb-loading-placeholder');
  placeholders.forEach(p => {
    if (message) {
      p.textContent = message;
      // @ts-ignore
      p.style.opacity = '0.4';
    } else {
      p.remove();
    }
  });
}

// Run immediately and periodically
document.addEventListener('DOMContentLoaded', initQbFilters);
setInterval(initQbFilters, 1000);
// Also hook into interactions
document.addEventListener('click', (e) => {
  const target = /** @type {Element} */ (e.target);
  if (target && (target.closest('summary') || target.closest('.header-drawer'))) {
    setTimeout(initQbFilters, 100);
  }
});

if (!customElements.get('header-drawer-new')) {
  customElements.define('header-drawer-new', HeaderDrawerNew);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
